import { cryptoAssets, fmtEur, fmtNumber } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

const cryptoActivity = [
  {
    date: "06 May",
    time: "08:34 CET",
    customer: "Anya Petrović",
    desc: "Customer top-up",
    asset: "USDC",
    network: "Solana",
    amount: 1008.4,
    eur: 940,
    direction: "in" as const,
    status: "success" as const,
    statusLabel: "Settled",
  },
  {
    date: "05 May",
    time: "20:26 CET",
    customer: "Yannis Kostaras",
    desc: "Customer top-up",
    asset: "USDC",
    network: "Solana",
    amount: 2253.05,
    eur: 2105,
    direction: "in" as const,
    status: "success" as const,
    statusLabel: "Settled",
  },
  {
    date: "05 May",
    time: "22:18 CET",
    customer: "Janka Tavárez",
    desc: "Pay-out",
    asset: "USDC",
    network: "Solana",
    amount: 909.5,
    eur: 850,
    direction: "out" as const,
    status: "success" as const,
    statusLabel: "Cleared",
  },
  {
    date: "04 May",
    time: "11:08 CET",
    customer: "Self · conversion",
    desc: "USDC → EUR",
    asset: "USDC",
    network: "Solana",
    amount: 5000,
    eur: 4670,
    direction: "out" as const,
    status: "success" as const,
    statusLabel: "Converted",
  },
  {
    date: "03 May",
    time: "14:55 CET",
    customer: "Tomáš Kovac",
    desc: "Customer top-up",
    asset: "EURC",
    network: "Ethereum",
    amount: 1500,
    eur: 1500,
    direction: "in" as const,
    status: "success" as const,
    statusLabel: "Settled",
  },
];

export function renderCrypto(ctx: Ctx) {
  const totalEur = cryptoAssets.reduce((a, x) => a + x.eurEquivalent, 0);

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Crypto<span class="page-eyebrow-rule"></span>Digital assets</span>
        <h1 class="page-title">Wallet, balances and conversions</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">Send</button>
        <button class="btn btn-primary">Convert ${icon("swap", 11)}</button>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Total Balance</span>
        <span class="summary-cell-value">${fmtEur(totalEur, { compact: true })}</span>
        <span class="summary-cell-note is-up">↑ 0.4% · 24h</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">USDC</span>
        <span class="summary-cell-value">${fmtNumber(cryptoAssets[0]!.balance)}</span>
        <span class="summary-cell-note">≈ ${fmtEur(cryptoAssets[0]!.eurEquivalent, { compact: true })}</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">EURC</span>
        <span class="summary-cell-value">${fmtNumber(cryptoAssets[1]!.balance)}</span>
        <span class="summary-cell-note">≈ ${fmtEur(cryptoAssets[1]!.eurEquivalent, { compact: true })}</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Volume · 7d</span>
        <span class="summary-cell-value">${fmtEur(332_512, { compact: true })}</span>
        <span class="summary-cell-note">18% of all pay-ins</span>
      </div>
    </section>

    <section class="grid-2-1">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Wallet</div>
            <h2 class="panel-title">Receive crypto</h2>
            <p class="panel-sub">Share these addresses with customers — Ivory Lion auto-routes incoming funds to the matching balance.</p>
          </div>
        </div>
        <div class="panel-body" style="display: flex; flex-direction: column; gap: 1.4rem;">
          ${cryptoAssets
            .map(
              (a) => `<div style="display: grid; grid-template-columns: 48px 1fr auto; gap: 1rem; align-items: center; padding: 1rem 1.15rem; background: var(--surface-canvas); border: 1px solid var(--line-default);">
            <span style="width: 48px; height: 48px; border-radius: 50%; background: ${a.symbol === "USDC" ? "var(--accent-soft)" : "var(--surface-feature)"}; color: ${a.symbol === "USDC" ? "var(--accent)" : "var(--ink-on-feature)"}; border: 1px solid var(--line-default); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.78rem;">${a.symbol}</span>
            <div style="line-height: 1.25;">
              <div style="font-weight: 600; font-size: 0.92rem; margin-bottom: 0.2rem;">${a.name} · <span style="font-weight: 500; color: var(--ink-muted);">${a.network}</span></div>
              <div style="font-family: ui-monospace, monospace; font-size: 0.78rem; color: var(--ink-secondary); letter-spacing: 0;">${a.address}</div>
            </div>
            <button class="btn btn-ghost btn-sm" type="button">Copy ${icon("copy", 11)}</button>
          </div>`,
            )
            .join("")}

          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 0.85rem; padding: 1rem 1.15rem; background: var(--surface-feature); border: 1px solid var(--line-default); font-variant-numeric: tabular-nums;">
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">USDC / EUR</div>
              <div style="font-weight: 600; font-size: 0.92rem; margin-top: 0.25rem;">€0.9337</div>
            </div>
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">EURC / EUR</div>
              <div style="font-weight: 600; font-size: 0.92rem; margin-top: 0.25rem;">€1.0000</div>
            </div>
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Network fee</div>
              <div style="font-weight: 600; font-size: 0.92rem; margin-top: 0.25rem;">€0.05</div>
            </div>
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Spread</div>
              <div style="font-weight: 600; font-size: 0.92rem; margin-top: 0.25rem;">0.20%</div>
            </div>
          </div>
        </div>
      </article>

      <article class="panel panel-feature">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Convert</div>
            <h2 class="panel-title">USDC → EUR</h2>
          </div>
        </div>
        <div class="panel-body">
          <div class="form-grid" style="grid-template-columns: 1fr;">
            <div class="field">
              <label class="field-label">From</label>
              <div class="field-control" style="display: flex; align-items: baseline; padding: 0.85rem 0.9rem; height: auto;">
                <span style="font-size: 1.45rem; font-weight: 200; letter-spacing: -0.025em; flex: 1; font-variant-numeric: tabular-nums;">5,000.00</span>
                <span style="font-size: 0.7rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--accent);">USDC</span>
              </div>
              <span class="field-help">Available · 52,418.20 USDC</span>
            </div>
            <div style="text-align: center; color: var(--ink-muted);">${icon("swap", 18)}</div>
            <div class="field">
              <label class="field-label">To</label>
              <div class="field-control" style="display: flex; align-items: baseline; padding: 0.85rem 0.9rem; height: auto;">
                <span style="font-size: 1.45rem; font-weight: 200; letter-spacing: -0.025em; flex: 1; font-variant-numeric: tabular-nums;">€4,668.50</span>
                <span style="font-size: 0.7rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--accent);">EUR</span>
              </div>
              <span class="field-help">Settles into operational balance</span>
            </div>
          </div>

          <div style="margin-top: 1.25rem; padding: 0.85rem 1rem; background: var(--surface-canvas); display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-variant-numeric: tabular-nums;">
            <span style="font-size: 0.7rem; color: var(--ink-muted);">Rate</span>
            <span style="font-size: 0.7rem; text-align: right; font-weight: 700;">1 USDC = €0.9337</span>
            <span style="font-size: 0.7rem; color: var(--ink-muted);">Fee · 0.20%</span>
            <span style="font-size: 0.7rem; text-align: right; font-weight: 700;">€9.34</span>
          </div>

          <button class="btn btn-primary btn-block" style="margin-top: 1.25rem;">
            Confirm conversion ${icon("arrowRight", 11)}
          </button>
        </div>
      </article>
    </section>

    <section class="panel" style="margin-top: 1.5rem;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Recent crypto activity</div>
          <h2 class="panel-title">Deposits, pay-outs and conversions</h2>
        </div>
        <a class="link-arrow" data-route="statements">All crypto activity ${icon("arrowRight", 13)}</a>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 90px 1.4fr 1fr 1fr 130px 30px;">
          <span>Date / Time</span>
          <span>Customer / Description</span>
          <span>Asset · Network</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
          <span></span>
        </div>
        ${cryptoActivity
          .map(
            (t) => `<div class="ledger-table-row" style="grid-template-columns: 90px 1.4fr 1fr 1fr 130px 30px;">
          <div class="col-date">
            <strong>${t.date}</strong>
            ${t.time}
          </div>
          <div class="col-name">
            <strong>${t.customer}</strong>
            <span>${t.desc}</span>
          </div>
          <div class="col-method">
            <span class="col-method-icon is-crypto">${t.asset[0]}</span>
            ${t.asset} · ${t.network}
          </div>
          <div class="col-amount is-${t.direction}">
            ${t.direction === "in" ? "+" : "−"}${fmtNumber(t.amount)} ${t.asset}
            <span class="col-amount-sub">≈ ${fmtEur(t.eur)}</span>
          </div>
          <div class="col-status">
            <span class="status-seal is-${t.status}"></span> ${t.statusLabel}
          </div>
          <div class="col-chevron">→</div>
        </div>`,
          )
          .join("")}
      </div>
    </section>
  `;

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });
}
